import { createContext } from 'react'

export type RealLanguage = 'en' | 'ar' | 'es' | 'fr' | 'zh'
export type Language = RealLanguage | 'system'

export interface LanguageConfig {
  code: RealLanguage
  nativeName: string
  dir: 'ltr' | 'rtl'
  locale: string
}

export const LANGUAGE_CONFIGS: Record<RealLanguage, LanguageConfig> = {
  en: { code: 'en', nativeName: 'English', dir: 'ltr', locale: 'en-US' },
  ar: { code: 'ar', nativeName: 'العربية', dir: 'rtl', locale: 'ar-SA' },
  es: { code: 'es', nativeName: 'Español', dir: 'ltr', locale: 'es-ES' },
  fr: { code: 'fr', nativeName: 'Français', dir: 'ltr', locale: 'fr-FR' },
  zh: { code: 'zh', nativeName: '中文（简体）', dir: 'ltr', locale: 'zh-CN' },
}

export interface SystemLanguageOption {
  code: 'system'
  nativeName: string
  dir: 'ltr'
}

export const SYSTEM_LANGUAGE_OPTION: SystemLanguageOption = {
  code: 'system',
  nativeName: 'System Default',
  dir: 'ltr',
}

export const ALL_LANGUAGE_OPTIONS = [
  SYSTEM_LANGUAGE_OPTION,
  ...Object.values(LANGUAGE_CONFIGS),
]

export function resolveSystemLanguage(): RealLanguage {
  try {
    const browserLang = navigator.language.split('-')[0].toLowerCase()
    const supported = Object.keys(LANGUAGE_CONFIGS) as RealLanguage[]
    return supported.find((l) => l === browserLang) ?? 'en'
  } catch {
    return 'en'
  }
}

export function resolveLanguage(lang: Language): RealLanguage {
  return lang === 'system' ? resolveSystemLanguage() : lang
}

export interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  isRTL: boolean
  dir: 'ltr' | 'rtl'
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  isRTL: false,
  dir: 'ltr',
})
